"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatRuntime = exports.summarizeHvacRunCycles = exports.HVAC_RUN_CYCLE_TIME_ZONE = void 0;
/**
 * Aggregation timezone. Run sessions are bucketed by the Pacific calendar day
 * so a day column here lines up with the SEER_DAILY aggregation's day.
 */
exports.HVAC_RUN_CYCLE_TIME_ZONE = 'America/Los_Angeles';
const DAY_MS = 24 * 60 * 60 * 1000;
const SECONDS_TO_MS = 1000;
const toLocalDate = (ms, timeZone) => new Date(ms).toLocaleDateString('en-CA', { timeZone });
const toWeekdayLabel = (ms, timeZone) => new Date(ms).toLocaleDateString('en-US', { weekday: 'short', timeZone });
/**
 * Collapses raw compressor run sessions into the per-day runtime summary the
 * HVAC "Run Time" cards render: total runtime, cycle count, average length of a
 * cycle, and the average efficiency measured while the system was actually
 * running.
 *
 * A cycle is attributed to the day it STARTED on, matching how the run-session
 * resolver stitches cycles across hour boundaries. Sessions outside the window
 * are ignored, so the caller's fetch range and `dayCount` should agree.
 *
 * `avgEER` is weighted by runtime — a 5-minute cycle shouldn't move the day's
 * number as much as an hour-long one — and skips cycles with a non-positive
 * EER, which is what the resolver emits when it had no usable CT/temperature
 * data for that cycle. Runtime and cycle counts still include those cycles: the
 * system genuinely ran, we just couldn't rate it.
 *
 * @param sessions - Run sessions from `useHvacRunSessions` (Unix seconds)
 * @param options - Window size, injected clock, and timezone overrides
 * @returns Per-day rows (oldest → newest), today's row, and window totals
 */
const summarizeHvacRunCycles = (sessions, options = {}) => {
    const { dayCount = 7, nowMs = Date.now(), timeZone = exports.HVAC_RUN_CYCLE_TIME_ZONE } = options;
    // Weighted-EER accumulators run alongside the public rows so a single pass
    // over the sessions fills both the day rows and the window totals.
    const eerWeighted = new Array(dayCount).fill(0);
    const eerWeight = new Array(dayCount).fill(0);
    let weekEerWeighted = 0;
    let weekEerWeight = 0;
    const days = [];
    for (let i = dayCount - 1; i >= 0; i--) {
        const ms = nowMs - i * DAY_MS;
        days.push({
            date: toLocalDate(ms, timeZone),
            label: toWeekdayLabel(ms, timeZone),
            isToday: i === 0,
            totalRuntimeSeconds: 0,
            cycleCount: 0,
            avgCycleSeconds: 0,
            avgEER: null,
        });
    }
    const indexByDate = new Map(days.map((d, idx) => [d.date, idx]));
    for (const session of sessions) {
        const idx = indexByDate.get(toLocalDate(session.runStartTime * SECONDS_TO_MS, timeZone));
        if (idx === undefined)
            continue;
        const duration = Math.max(0, session.durationSeconds);
        days[idx].totalRuntimeSeconds += duration;
        days[idx].cycleCount += 1;
        if (session.avgEER > 0 && duration > 0) {
            eerWeighted[idx] += session.avgEER * duration;
            eerWeight[idx] += duration;
            weekEerWeighted += session.avgEER * duration;
            weekEerWeight += duration;
        }
    }
    let weekRuntime = 0;
    let weekCycles = 0;
    days.forEach((day, idx) => {
        day.avgCycleSeconds = day.cycleCount > 0 ? day.totalRuntimeSeconds / day.cycleCount : 0;
        day.avgEER = eerWeight[idx] > 0 ? eerWeighted[idx] / eerWeight[idx] : null;
        weekRuntime += day.totalRuntimeSeconds;
        weekCycles += day.cycleCount;
    });
    return {
        days,
        today: days[days.length - 1],
        week: {
            totalRuntimeSeconds: weekRuntime,
            cycleCount: weekCycles,
            avgCycleSeconds: weekCycles > 0 ? weekRuntime / weekCycles : 0,
            avgEER: weekEerWeight > 0 ? weekEerWeighted / weekEerWeight : null,
        },
        isEmpty: weekCycles === 0,
    };
};
exports.summarizeHvacRunCycles = summarizeHvacRunCycles;
/**
 * Formats a runtime in seconds as a compact `4h 12m` / `36m` string.
 * @param seconds - Duration in seconds
 * @param emptyPlaceholder - Rendered when the duration is zero or negative
 * @returns Human-readable duration
 */
const formatRuntime = (seconds, emptyPlaceholder = '—') => {
    if (!Number.isFinite(seconds) || seconds <= 0)
        return emptyPlaceholder;
    const totalMinutes = Math.round(seconds / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h > 0)
        return `${h}h ${m}m`;
    // Sub-minute cycles are real (short-cycling) — don't collapse them to "0m".
    return totalMinutes > 0 ? `${m}m` : '<1m';
};
exports.formatRuntime = formatRuntime;
